import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {zodResolver} from "@hookform/resolvers/zod";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "sonner";
import {CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Box, Colors, Container, IconAlert, IconButton, IconClose, Text} from "@bounce/shared/ui";
import {Button} from "../../ui/components";
import {useCountSegmentAccounts} from "../../apis/requests/segments/countSegmentAccounts";
import {useGetSegmentsSchema} from "../../apis/requests/segments/getSegmentsSchema";
import {
  ConditionGroup,
  ReachSegment,
  ReachSegmentCreatePayload,
  SegmentPageKind,
} from "../../apis/requests/segments/types";
import CategoryFallbackCard from "./segment-form/CategoryFallbackCard";
import ConditionsCard from "./segment-form/ConditionsCard";
import DetailsCard from "./segment-form/DetailsCard";
import SegmentCategoryBucketsCard from "./segment-form/SegmentCategoryBucketsCard";
import {
  DEFAULT_SEGMENT_TYPE,
  EMPTY_CONDITIONS,
  NO_SEGMENT_CATEGORY,
  VALIDATION_SEGMENT_TYPE,
} from "./segment-form/constants";
import {buildConditionsPayload, firstErrorMessage, segmentCategoryFromForm} from "./segment-form/helpers";
import {categoryFallbackFormSchema, formSchema, FormValues} from "./segment-form/schema";

export type SegmentDialogProps = {
  segment?: ReachSegment;
  pageKind: SegmentPageKind;
  segments: ReachSegment[];
  onStage: (payload: ReachSegmentCreatePayload) => void;
  onOpenSegment: (segment: ReachSegment) => void;
};

const mapGroupsFromApi = (conditions: ConditionGroup): FormValues["groups"] => {
  if (!conditions.rules.length) {
    return [{name: "", match_mode: "all", conditions: []}];
  }
  return conditions.rules.map((group) => ({
    name: group.name ?? "",
    match_mode: group.match_mode,
    conditions: group.rules.map((c) => ({
      parameter: c.parameter,
      operator: c.operator,
      value: c.value,
    })),
  }));
};

const NEW_SEGMENT_FORM_KEY = "new";
const SEGMENT_NOUN = "segment";
const BUCKET_NOUN = "bucket";

type SegmentDialogFormProps = SegmentDialogProps & {onClose: () => void};

const SegmentDialogForm = ({
  segment: existing,
  pageKind,
  segments,
  onStage,
  onOpenSegment,
  onClose,
}: SegmentDialogFormProps) => {
  const isEdit = !!existing;
  const isCategoryFallback = existing?.is_category_fallback ?? false;
  const {data: schema} = useGetSegmentsSchema();
  const {mutateAsync: countAsync, data: countData, isPending: isCounting} = useCountSegmentAccounts();

  const parameters = schema?.parameters ?? [];
  const defaultType = pageKind === "validation" ? VALIDATION_SEGMENT_TYPE : DEFAULT_SEGMENT_TYPE;

  const methods = useForm<FormValues>({
    resolver: zodResolver(isCategoryFallback ? categoryFallbackFormSchema : formSchema),
    values: {
      name: existing?.name ?? "",
      description: existing?.description ?? "",
      type: existing?.type ?? defaultType,
      segment_category: existing?.segment_category ?? NO_SEGMENT_CATEGORY,
      group_match_mode: existing?.conditions.match_mode ?? "all",
      groups: existing?.conditions
        ? mapGroupsFromApi(existing.conditions)
        : [{name: "", match_mode: "all" as const, conditions: []}],
    },
  });
  const {
    handleSubmit,
    watch,
    trigger,
    getValues,
    formState: {isDirty, errors},
  } = methods;

  const nameValue = watch("name");
  const selectedSegmentCategory = segmentCategoryFromForm({
    type: watch("type"),
    segment_category: watch("segment_category"),
  });
  const categoryBuckets = selectedSegmentCategory
    ? segments.filter((s) => s.segment_category === selectedSegmentCategory)
    : [];
  const issue = firstErrorMessage(errors);
  const itemNoun = selectedSegmentCategory ? BUCKET_NOUN : SEGMENT_NOUN;

  const countMatchingAccounts = async () => {
    if (isCategoryFallback && existing) {
      await countAsync({conditions: existing.conditions, segmentId: existing.id});
      return;
    }
    const conditionsValid = await trigger("groups");
    if (!conditionsValid) return;
    await countAsync({conditions: buildConditionsPayload(getValues(), parameters)});
  };

  const onPreviewCount = async () => {
    try {
      await countMatchingAccounts();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to count matching accounts");
    }
  };

  const onSubmit = handleSubmit((values) => {
    onStage({
      name: values.name.trim(),
      description: values.description.trim() || null,
      priority: existing?.priority ?? 0,
      type: values.type,
      segment_category: isCategoryFallback ? existing?.segment_category ?? null : segmentCategoryFromForm(values),
      is_category_fallback: isCategoryFallback,
      conditions: isCategoryFallback ? EMPTY_CONDITIONS : buildConditionsPayload(values, parameters),
    });
    onClose();
  });

  const stageDisabled = !nameValue?.trim() || (isEdit && !isDirty);

  return (
    <>
      <DialogTitle sx={{p: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        <Text size={20} variant="interDisplay600" sx={{color: Colors.grey._800}}>
          {isEdit ? `Edit ${itemNoun}` : `New ${itemNoun}`}
        </Text>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: Colors.grey._500,
            "&:hover": {backgroundColor: Colors.tertiary._200},
          }}
        >
          <IconClose width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{p: "0 24px 24px"}}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={onSubmit} sx={{display: "flex", flexDirection: "column", gap: 2}}>
            <DetailsCard pageKind={pageKind} isCategoryFallback={isCategoryFallback} />
            {selectedSegmentCategory && (
              <SegmentCategoryBucketsCard
                segmentCategory={selectedSegmentCategory}
                currentName={nameValue ?? ""}
                buckets={categoryBuckets}
                currentSegmentId={existing?.id}
                onOpenBucket={onOpenSegment}
                switchingBlocked={isDirty}
              />
            )}
            {isCategoryFallback ? <CategoryFallbackCard /> : <ConditionsCard parameters={parameters} />}
          </Box>
        </FormProvider>
      </DialogContent>
      <DialogActions
        sx={{p: "16px 24px", justifyContent: "space-between", borderTop: `1px solid ${Colors.tertiary._200}`}}
      >
        <Container row sx={{gap: 1.5, alignItems: "center", minHeight: 32}}>
          <Button
            buttonType="outlined-primary"
            size="small"
            onClick={onPreviewCount}
            disabled={isCounting}
            sx={{
              borderColor: Colors.tertiary._300,
              color: Colors.grey._800,
              "&:hover": {borderColor: Colors.tertiary._400, backgroundColor: Colors.grey._100},
            }}
          >
            {isCounting ? <CircularProgress size={16} /> : "Preview matching accounts"}
          </Button>
          {issue ? (
            <Container row sx={{gap: 1, alignItems: "center"}}>
              <IconAlert width={18} height={18} color={Colors.red._900} />
              <Text size={13} sx={{color: Colors.red._900}}>
                {issue}
              </Text>
            </Container>
          ) : (
            countData && (
              <Text size={13} sx={{color: Colors.grey._700}}>
                {countData.count.toLocaleString()} accounts match
              </Text>
            )
          )}
        </Container>
        <Container row sx={{gap: 1}}>
          <Button
            buttonType="outlined-primary"
            size="small"
            onClick={onClose}
            sx={{
              borderColor: Colors.tertiary._300,
              color: Colors.grey._800,
              "&:hover": {borderColor: Colors.tertiary._400, backgroundColor: Colors.grey._100},
            }}
          >
            Cancel
          </Button>
          <Button buttonType="contained-primary" size="small" onClick={onSubmit} disabled={stageDisabled}>
            {isEdit ? "Apply changes" : `Add ${itemNoun}`}
          </Button>
        </Container>
      </DialogActions>
    </>
  );
};

const SegmentDialog = NiceModal.create((props: SegmentDialogProps) => {
  const modal = useModal();
  const close = () => modal.hide();
  return (
    <Dialog
      open={modal.visible}
      onClose={close}
      slotProps={{
        transition: {onExited: () => modal.remove()},
        paper: {
          sx: {
            borderRadius: "16px",
            maxWidth: 900,
            width: "100%",
            backgroundColor: Colors.bw.white,
          },
        },
      }}
      maxWidth={false}
    >
      <SegmentDialogForm key={props.segment?.id ?? NEW_SEGMENT_FORM_KEY} {...props} onClose={close} />
    </Dialog>
  );
});

export default SegmentDialog;
